import { TenantHandlerParamsType } from '../../../services/tenantHandler/strategy/external-types';
import { TenantStrategyEnum } from '../../../services/tenantHandler/strategy/TenantStrategyEnum';

/**
 * Handles tenant-related manifest enhancements
 * Provides retrocompatibility for organization and customer selectors
 */
export class ManifestTenantEnhancer {
  private static readonly DEFAULT_TENANT_HANDLER_KEY = 'default';

  /**
   * Adapts organization and customer selector configurations into tenant handler format
   * Provides backward compatibility for legacy selector configurations
   */
  static adaptOrgAndCustomerSelectorIntoTenantHandler(manifest: any): void {
    if (this.isTenantHandlerAlreadyActive(manifest)) {
      return;
    }

    const orgSelectorSettings = this.extractOrgSelectorSettings(manifest);

    if (orgSelectorSettings?.enable) {
      const tenantHandlerConfig =
        this.createTenantHandlerConfig(orgSelectorSettings);
      this.applyTenantHandlerConfig(manifest, tenantHandlerConfig);

      const customerSelectorSettings =
        this.extractCustomerSelectorSettings(manifest);
      if (customerSelectorSettings?.enable) {
        this.addCustomerSelectorBehavior(tenantHandlerConfig);
      }
    }
  }

  /**
   * Checks if tenant handler is already configured
   */
  private static isTenantHandlerAlreadyActive(manifest: any): boolean {
    return !!manifest?.services?.tenantHandler?.enable;
  }

  /**
   * Extracts organization selector settings from manifest
   */
  private static extractOrgSelectorSettings(manifest: any) {
    return manifest?.navigation?.header?.domainSelector;
  }

  /**
   * Extracts customer selector settings from manifest
   *  TODO: This is very fragile, the portal could change the assetReference to any other name.
   */
  private static extractCustomerSelectorSettings(manifest: any) {
    return manifest?.navigation?.header?.widgets?.find(
      (widget) =>
        widget.assetReference === '@jarvis/react-shell-customer-selector' &&
        widget.enable
    );
  }

  /**
   * Creates tenant handler configuration from organization selector settings
   */
  private static createTenantHandlerConfig(
    orgSelectorSettings: any
  ): TenantHandlerParamsType {
    return {
      key: this.DEFAULT_TENANT_HANDLER_KEY,
      strategy: TenantStrategyEnum.stratusOrganization,
      options: {
        fallback: orgSelectorSettings?.fallback,
        filter: orgSelectorSettings?.filter
      }
    };
  }

  /**
   * Applies tenant handler configuration to manifest
   */
  private static applyTenantHandlerConfig(
    manifest: any,
    tenantHandlerConfig: TenantHandlerParamsType
  ): void {
    manifest.services = manifest.services || {};
    manifest.services.tenantHandler = {
      enable: true,
      globalTenantHandler: tenantHandlerConfig.key,
      tenantHandlerList: [tenantHandlerConfig]
    };
  }

  /**
   * Adds customer selector behavior as sub-tenant configuration
   */
  private static addCustomerSelectorBehavior(
    tenantHandlerConfig: TenantHandlerParamsType
  ): void {
    const customerSelectorConfig: TenantHandlerParamsType = {
      strategy: TenantStrategyEnum.stratusCustomer
    };

    tenantHandlerConfig.subTenant = customerSelectorConfig;
  }
}
